import React, { useEffect, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { Plus, Trash2, Save } from "lucide-react";
import { BOARD_ROLES } from "@/lib/constants";
import { toast } from "sonner";

const blank = () => ({
  full_name: "", role: "Reviewer", specialty: "", institution: "",
  city: "", country: "", email: "", orcid: "", photo_url: "", bio: "", display_order: 100,
});

export default function AdminEditorialBoard() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => {
    const { data } = await api.get("/editorial-board");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (sel.id) await api.put(`/admin/editorial-board/${sel.id}`, sel);
      else await api.post("/admin/editorial-board", sel);
      toast.success("Member saved");
      setSel(null); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete ${m.full_name}?`)) return;
    await api.delete(`/admin/editorial-board/${m.id}`);
    load();
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setSel({ ...sel, photo_url: data.stored_path });
  };

  return (
    <div data-testid="admin-board">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-3xl text-[#001F3F]">Editorial Board</h1>
        <button onClick={() => setSel(blank())} className="bg-[#001F3F] text-white px-5 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2"><Plus className="w-4 h-4" /> New Member</button>
      </div>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mb-6"></div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm font-sans">
          <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#001F3F]">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Specialty</th><th className="text-left px-4 py-3">Institution</th><th></th></tr>
          </thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={5} className="text-center py-12 text-gray-500">No editorial board members yet.</td></tr> :
              list.map((m) => (
                <tr key={m.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-[#001F3F]">{m.full_name}</td>
                  <td className="px-4 py-3 text-xs uppercase text-[#2BA6A6]">{m.role}</td>
                  <td className="px-4 py-3 text-gray-600">{m.specialty}</td>
                  <td className="px-4 py-3 text-gray-600">{m.institution}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSel(m)} className="text-[#2BA6A6] text-xs uppercase mr-3">Edit</button>
                    <button onClick={() => del(m)} className="text-red-500 text-xs"><Trash2 className="w-3 h-3 inline" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-6">
          <div className="bg-white max-w-2xl w-full">
            <div className="p-5 bg-[#001F3F] text-white flex justify-between">
              <div className="font-serif text-xl">{sel.id ? "Edit Member" : "New Member"}</div>
              <button onClick={() => setSel(null)} className="text-white/80">✕</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-3">
              <F label="Full Name" full value={sel.full_name} onChange={(v) => setSel({ ...sel, full_name: v })} />
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Role</label>
                <select value={sel.role} onChange={(e) => setSel({ ...sel, role: e.target.value })} className="w-full px-3 py-2 border border-gray-200 text-sm">
                  {BOARD_ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <F label="Specialty" value={sel.specialty} onChange={(v) => setSel({ ...sel, specialty: v })} />
              <F label="Institution" value={sel.institution} onChange={(v) => setSel({ ...sel, institution: v })} />
              <F label="City" value={sel.city} onChange={(v) => setSel({ ...sel, city: v })} />
              <F label="Country" value={sel.country} onChange={(v) => setSel({ ...sel, country: v })} />
              <F label="Email" value={sel.email} onChange={(v) => setSel({ ...sel, email: v })} />
              <F label="ORCID" value={sel.orcid} onChange={(v) => setSel({ ...sel, orcid: v })} />
              <F label="Display Order" type="number" value={sel.display_order} onChange={(v) => setSel({ ...sel, display_order: parseInt(v) || 100 })} />
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Photo</label>
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                {sel.photo_url && <img src={assetUrl(sel.photo_url)} alt="" className="mt-2 w-24 h-24 rounded-full object-cover border" />}
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Bio</label>
                <textarea rows={3} value={sel.bio || ""} onChange={(e) => setSel({ ...sel, bio: e.target.value })} className="w-full px-3 py-2 border border-gray-200 text-sm" />
              </div>
              <div className="col-span-2 pt-2">
                <button onClick={save} className="bg-[#001F3F] text-white px-5 py-2 text-xs uppercase tracking-wider hover:bg-[#2BA6A6]"><Save className="w-3 h-3 inline mr-1" /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, full, type = "text" }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 text-sm" />
    </div>
  );
}
